/**
 * Shared hreflang normalization helpers (ECOM-2433).
 *
 * There is no single hreflang builder in the codebase — the static, PDP, PLP,
 * and CMS builders each construct their own alternates. These pure helpers give
 * all four one consistent rule for the three recurring defect classes:
 * trailing slashes, x-default resolution, and query-param suppression.
 *
 * The module is intentionally dependency-free (no locale config, no Nuxt
 * runtime) so it can be unit-tested in isolation and called from both the
 * shared layer and the per-app builders.
 */

/** hreflang member as emitted by every builder: an ISO hreflang code + its href. */
export interface HreflangMember {
  hreflang: string
  href?: string
}

/**
 * The only query key that does NOT suppress hreflang. Pagination keeps its
 * hreflang group (ECOM-1495); every other query key (filters, sort, etc.)
 * marks a non-canonical view whose hreflang group would be inconsistent.
 */
const NON_SUPPRESSING_QUERY_KEYS = new Set(['page'])

/**
 * True when the route query carries any key other than `page`, meaning the
 * caller should emit no hreflang tags at all. Canonical generation is separate
 * and continues to strip params independently.
 */
export function hasSuppressingQuery(
  query: Record<string, unknown> | null | undefined,
): boolean {
  if (!query) {
    return false
  }
  return Object.keys(query).some((key) => !NON_SUPPRESSING_QUERY_KEYS.has(key))
}

/**
 * Removes a trailing slash from an hreflang href's path so it resolves to 200
 * instead of a 301 redirect (e.g. `/de/c/inspiration/` → `/de/c/inspiration`,
 * homepage `/de/` → `/de`). The origin root (`https://host/`) is left untouched,
 * and any query/hash is preserved. Non-absolute or unparseable input is
 * returned unchanged so callers never lose a href.
 */
export function stripHreflangTrailingSlash(href: string): string {
  if (!href) {
    return href
  }
  try {
    const url = new URL(href)
    if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
      url.pathname = url.pathname.replace(/\/+$/, '')
      return url.toString()
    }
    return href
  } catch {
    return href
  }
}

/**
 * Resolves the x-default href: the de-DE member's href verbatim, or `null` when
 * no German member exists (never a synthesized `/de/{slug}` guess).
 *
 * Callers MUST include the current page's self-reference in `members`. On German
 * pages the de-DE member IS the self-reference, because every builder excludes
 * the current locale from its alternates map — passing only the alternate set
 * would return `null` and strip x-default from every German page.
 */
export function pickXDefaultHref(
  members: ReadonlyArray<HreflangMember>,
): string | null {
  const german = members.find((m) => m.hreflang === 'de-DE' && !!m.href)
  return german?.href ?? null
}
